import * as React from 'react'
import { cn } from '../../lib/utils'
import { InputContainer, inputContainerVariants } from './input-container'
import { InputBase } from './input-base'
import { type VariantProps } from 'class-variance-authority'
import { Typography } from '../typography'

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputContainerVariants> {
  'data-error'?: boolean
  icon?: React.ReactNode
  startIcon?: React.ReactNode
  startLabel?: string
  endIcon?: React.ReactNode
  endLabel?: string
  rows?: number
  as?: 'input' | 'textarea'
  noFocusStyle?: boolean
}

const convertToEnglishNumbers = (value: string): string => {
  const persianNumbers = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']
  const arabicNumbers = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩']

  let result = value
  persianNumbers.forEach((num, index) => {
    result = result.replace(new RegExp(num, 'g'), index.toString())
  })
  arabicNumbers.forEach((num, index) => {
    result = result.replace(new RegExp(num, 'g'), index.toString())
  })
  return result
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      icon,
      startIcon,
      startLabel,
      endLabel,
      endIcon,
      variant,
      inputSize,
      className,
      value,
      onChange,
      rows,
      as,
      noFocusStyle,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = React.useState(false)
    const [endPadding, setEndPadding] = React.useState(0)
    const endLabelRef = React.useRef<HTMLSpanElement>(null)

    React.useLayoutEffect(() => {
      const updateWidth = () => {
        let totalWidth = 0

        if (endLabel && endLabelRef.current) {
          totalWidth += endLabelRef.current.offsetWidth - 24
        }

        setEndPadding(totalWidth)
      }

      updateWidth()

      const resizeObserver = new ResizeObserver(() => {
        updateWidth()
      })

      if (endLabelRef.current) {
        resizeObserver.observe(endLabelRef.current)
      }

      return () => {
        resizeObserver.disconnect()
      }
    }, [endIcon, endLabel])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        const convertedValue = convertToEnglishNumbers(e.target.value)
        e.target.value = convertedValue
        onChange(e)
      }
    }

    return (
      <InputContainer
        variant={variant}
        inputSize={inputSize}
        data-error={props['data-error']}
        data-focused={noFocusStyle ? false : isFocused}
        data-read-only={props.readOnly ? true : false}
        data-disabled={props.disabled ? true : false}
        className={cn(className)}
      >
        <div className='relative w-full flex items-center '>
          {icon && (
            <span className='flex items-center justify-center h-full px-sm '>
              {icon}
            </span>
          )}
          <span className='hidden'>{props.disabled ? 'true' : 'false'}</span>
          {startIcon && (
            <span className='flex items-center justify-center h-full px-sm '>
              {startIcon}
            </span>
          )}
          {startLabel && (
            <Typography
              variant='bdi'
              weight='regular'
              color='text-neutral-surface-80'
              size='xs'
              className='flex items-center justify-center h-full px-sm'
            >
              {startLabel}
            </Typography>
          )}
          <InputBase
            className={cn(className)}
            ref={ref}
            {...props}
            onChange={handleChange}
            onFocus={(e) => {
              setIsFocused(true)
              props.onFocus?.(e as React.FocusEvent<HTMLInputElement>)
            }}
            onBlur={(e) => {
              setIsFocused(false)
              props.onBlur?.(e as React.FocusEvent<HTMLInputElement>)
            }}
            value={value || ''}
            {...(rows && { rows })}
            {...(as && { as })}
            style={{
              ...props.style,
              paddingRight: endPadding > 0 ? `${endPadding}px` : undefined,
            }}
          />
          {endLabel && (
            <span ref={endLabelRef}>
              <Typography
                variant='bdi'
                weight='regular'
                color='text-neutral-surface-80'
                size='xs'
                className='flex items-center justify-center h-full px-sm'
              >
                {endLabel}
              </Typography>
            </span>
          )}
          {endIcon && (
            <span className='flex items-center justify-center h-full'>
              {endIcon}
            </span>
          )}
        </div>
      </InputContainer>
    )
  },
)

Input.displayName = 'Input'

export { Input }
